import { useState, useEffect } from 'react';
import { FaEnvelope, FaTimes, FaPlus } from 'react-icons/fa';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const isValidEmail = (value) => EMAIL_PATTERN.test((value || '').trim());

/**
 * Asks the admin whether to tell the supplier an invoice was voided, and where to send it.
 *
 * The invoice is already voided by the time this opens — skipping only skips the email. Addresses
 * are prefilled from the supplier record and editable for this send only; nothing is saved back.
 */
const VoidNoticeModal = ({
  isOpen,
  invoiceNumber,
  supplierName,
  suggestedRecipients,
  recipientReason,
  sending,
  result,
  onSend,
  onSkip,
}) => {
  const [addresses, setAddresses] = useState(['']);

  // Reopening for a different invoice must not inherit the previous one's addresses.
  useEffect(() => {
    if (isOpen) {
      setAddresses(suggestedRecipients?.length ? [...suggestedRecipients] : ['']);
    }
  }, [isOpen, suggestedRecipients]);

  if (!isOpen) return null;

  const updateAddress = (index, value) => {
    setAddresses(prev => prev.map((a, i) => (i === index ? value : a)));
  };
  const removeAddress = (index) => {
    setAddresses(prev => (prev.length === 1 ? [''] : prev.filter((_, i) => i !== index)));
  };
  const addAddress = () => setAddresses(prev => [...prev, '']);

  const filled = addresses.map(a => a.trim()).filter(Boolean);
  const invalid = filled.filter(a => !isValidEmail(a));
  const canSend = filled.length > 0 && invalid.length === 0 && !sending;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-full overflow-y-auto">
        <div className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center mb-2">
            <FaEnvelope className="text-jims-cyan mr-2 flex-shrink-0" />
            Notify the supplier?
          </h3>

          <p className="text-sm text-gray-600 mb-4">
            Invoice <span className="font-medium text-gray-900">{invoiceNumber || 'this invoice'}</span> has
            been voided{supplierName ? <> for <span className="font-medium text-gray-900">{supplierName}</span></> : null}.
            Sending is optional — the invoice stays voided either way.
          </p>

          {result ? (
            <div
              className={`text-sm rounded p-3 mb-4 border ${
                result.sent
                  ? 'text-green-800 bg-green-50 border-green-200'
                  : 'text-red-800 bg-red-50 border-red-200'
              }`}
            >
              {result.message}
            </div>
          ) : (
            <>
              {recipientReason && !suggestedRecipients?.length && (
                <p className="text-sm text-amber-800 bg-amber-50 border border-amber-300 rounded p-3 mb-3">
                  {recipientReason} Enter an address below to notify someone anyway.
                </p>
              )}

              <label className="block text-sm font-medium text-gray-700 mb-1">Send to</label>
              <div className="space-y-2 mb-2">
                {addresses.map((address, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <input
                      type="email"
                      value={address}
                      onChange={(e) => updateAddress(index, e.target.value)}
                      placeholder="name@example.com"
                      className={`flex-1 input-field ${
                        address.trim() && !isValidEmail(address) ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    <button
                      type="button"
                      onClick={() => removeAddress(index)}
                      aria-label="Remove this address"
                      className="p-2 text-gray-400 hover:text-red-600 focus:outline-none focus:ring-2 focus:ring-red-400 rounded"
                    >
                      <FaTimes />
                    </button>
                  </div>
                ))}
              </div>

              <button
                type="button"
                onClick={addAddress}
                className="inline-flex items-center gap-1.5 text-sm text-jims-cyan hover:underline mb-2"
              >
                <FaPlus className="text-xs" /> Add another address
              </button>

              {invalid.length > 0 && (
                <p className="input-error">Check these addresses: {invalid.join(', ')}</p>
              )}
            </>
          )}

          <div className="flex justify-end space-x-3 mt-4">
            <button
              type="button"
              onClick={onSkip}
              disabled={sending}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 border border-gray-300 rounded-md shadow-sm hover:bg-gray-300 disabled:opacity-50"
            >
              {result ? 'Close' : "Don't send"}
            </button>
            {!result && (
              <button
                type="button"
                onClick={() => onSend(filled)}
                disabled={!canSend}
                className="px-4 py-2 text-sm font-medium text-white bg-jims-cyan border border-cyan-700 rounded-md shadow-sm hover:opacity-90 disabled:opacity-50"
              >
                {sending ? 'Sending…' : `Send notification${filled.length > 1 ? ` (${filled.length})` : ''}`}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VoidNoticeModal;
